<template>
    <!--
        Listagem paginada padrão das telas de cadastro (usuários, empresas, unidades, filtros...).
        Recebe da API { [chaveDados]: paginator do Laravel, CamposTabelaExibicao: [...] } e oferece:
        busca no servidor, itens por página, carregamento inline, estados vazio/erro,
        status como badge e linha clicável (abre a visualização).
        O v-ec-datatable (modo servidor) acrescenta exibição de colunas, densidade,
        filtro por coluna (?filtros[campo]=texto), exportação e cartões no celular.
        Estilos em resources/sass/_tabelas.scss.
    -->
    <div class="ec-listagem">
        <div class="ec-listagem__toolbar">
            <div class="ec-listagem__acoes">
                <slot name="botaonovo"></slot>
            </div>

            <div class="ec-listagem__filtros">
                <div v-if="pesquisavel" class="ec-busca">
                    <b-icon icon="search" class="ec-busca__icone" aria-hidden="true"></b-icon>
                    <input
                        type="search"
                        class="form-control form-control-sm ec-busca__campo"
                        :placeholder="placeholderBusca"
                        :aria-label="placeholderBusca"
                        v-model="busca"
                        @input="buscarComAtraso"
                        @keyup.esc="limparBusca"
                    >
                    <button v-if="busca" type="button" class="ec-busca__limpar" title="Limpar busca" aria-label="Limpar busca" @click="limparBusca">
                        <b-icon icon="x" aria-hidden="true"></b-icon>
                    </button>
                </div>

                <label class="ec-por-pagina">
                    <span>Exibir</span>
                    <select class="custom-select custom-select-sm" v-model.number="porPagina" @change="carregar(1)">
                        <option v-for="opcao in opcoesPorPagina" :key="opcao" :value="opcao">{{ opcao }}</option>
                    </select>
                </label>

                <!-- botões "Exibição" e "Exportar" do v-ec-datatable -->
                <div class="ec-listagem__ferramentas"></div>
            </div>
        </div>

        <div class="table-responsive ec-listagem__rolagem" :class="{ 'ec-listagem__rolagem--carregando': carregando && linhas.length }">
            <table v-ec-datatable="opcoesTabela" class="table table-sm table-hover ec-tabela mb-0" :aria-busy="carregando ? 'true' : 'false'">
                <thead class="ec-thead">
                    <tr>
                        <slot name="cabecalho-antes"></slot>
                        <th
                            v-for="coluna in colunas"
                            :key="coluna.nomecampo"
                            :data-ec-prioridade="coluna.nomecampo === 'ativo' ? 2 : null"
                            :class="['text-' + alinhamento(coluna), 'ec-tabela__ordenavel']"
                            :aria-sort="ordenar === coluna.nomecampo ? (direcao === 'asc' ? 'ascending' : 'descending') : 'none'"
                            tabindex="0"
                            :title="'Ordenar por ' + coluna.descricaocoluna"
                            @click="ordenarPor(coluna.nomecampo)"
                            @keydown.enter.prevent="ordenarPor(coluna.nomecampo)"
                            @keydown.space.prevent="ordenarPor(coluna.nomecampo)"
                        >
                            {{ coluna.descricaocoluna }}
                        </th>
                        <th class="text-right ec-tabela__col-acoes"><span class="sr-only">Ações</span></th>
                    </tr>
                </thead>

                <tbody>
                    <!-- primeira carga: linhas "esqueleto" no lugar do modal bloqueante -->
                    <template v-if="carregando && !linhas.length">
                        <tr v-for="n in Math.min(porPagina, 5)" :key="'sk' + n" class="ec-tabela__esqueleto">
                            <td v-for="c in totalColunas" :key="c"><span></span></td>
                        </tr>
                    </template>

                    <tr v-else-if="erro">
                        <td :colspan="totalColunas" class="ec-tabela__estado">
                            <b-icon icon="exclamation-triangle" font-scale="2" class="text-danger"></b-icon>
                            <p>Não foi possível carregar os registros.</p>
                            <button type="button" class="btn btn-outline-primary btn-sm" @click="carregar()">Tentar novamente</button>
                        </td>
                    </tr>

                    <tr v-else-if="!linhas.length">
                        <td :colspan="totalColunas" class="ec-tabela__estado">
                            <b-icon :icon="buscaAplicada || temFiltroColuna ? 'search' : 'inbox'" font-scale="2"></b-icon>
                            <p v-if="buscaAplicada">Nenhum resultado para <b>"{{ buscaAplicada }}"</b><template v-if="temFiltroColuna"> com os filtros aplicados</template>.</p>
                            <p v-else-if="temFiltroColuna">Nenhum resultado para os filtros aplicados.</p>
                            <p v-else>{{ textoVazio }}</p>
                            <button v-if="buscaAplicada" type="button" class="btn btn-outline-primary btn-sm" @click="limparBusca">Limpar busca</button>
                        </td>
                    </tr>

                    <template v-else>
                        <tr
                            v-for="(item, i) in linhas"
                            :key="item.id || i"
                            :class="{ 'ec-tabela__linha-link': !!linkLinha }"
                            @click="abrirLinha(item, $event)"
                        >
                            <slot name="celulas-antes" :item="item"></slot>
                            <td v-for="coluna in colunas" :key="coluna.nomecampo" :class="'text-' + alinhamento(coluna)" :data-label="coluna.descricaocoluna">
                                <span v-if="coluna.nomecampo === 'ativo'" class="ec-status" :class="'ec-status--' + (ehAtivo(item.ativo) ? 'ativo' : 'inativo')">
                                    {{ item.ativo }}
                                </span>
                                <slot v-else name="celula" :item="item" :coluna="coluna" :valor="item[coluna.nomecampo]">
                                    <span :class="{ 'ec-tabela__vazio': valorVazio(item[coluna.nomecampo]) }">
                                        {{ valorVazio(item[coluna.nomecampo]) ? '—' : item[coluna.nomecampo] }}
                                    </span>
                                </slot>
                            </td>
                            <td class="ec-tabela__col-acoes" data-label="Ações">
                                <div class="ec-acoes">
                                    <slot name="acoes" :item="item"></slot>
                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>

        <div v-if="paginacao.total > 0" class="ec-listagem__rodape">
            <span class="ec-listagem__info">
                Mostrando <b>{{ paginacao.from }}–{{ paginacao.to }}</b> de <b>{{ paginacao.total }}</b> {{ rotuloItens }}
            </span>

            <nav v-if="paginacao.last_page > 1" aria-label="Paginação">
                <ul class="pagination pagination-sm mb-0">
                    <li v-for="(link, key) in links" :key="key" class="page-item" :class="{ active: link.active, disabled: !link.url || carregando }">
                        <button
                            type="button"
                            class="page-link"
                            :disabled="!link.url"
                            :aria-current="link.active ? 'page' : null"
                            :aria-label="link.rotulo"
                            @click="irPara(link)"
                            v-html="link.label"
                        ></button>
                    </li>
                </ul>
            </nav>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        // rota relativa da API (sem /api/v1), ex.: /Users/User
        urlSistema: { type: String, required: true },
        // chave do paginator no JSON de resposta ('Pessoas' ou 'Filtros')
        chaveDados: { type: String, default: 'Pessoas' },
        rotuloItens: { type: String, default: 'registros' },
        textoVazio: { type: String, default: 'Nenhum registro cadastrado.' },
        placeholderBusca: { type: String, default: 'Buscar...' },
        pesquisavel: { type: Boolean, default: true },
        // colunas extras renderizadas pelo slot celulas-antes (para o colspan dos estados)
        colunasExtras: { type: Number, default: 0 },
        // função (item) => url aberta ao clicar na linha; null desativa
        linkLinha: { type: Function, default: null },
        // rótulos (descricaocoluna) das colunas ocultas por padrão; o usuário pode exibi-las
        colunasOcultas: { type: Array, default: () => [] },
    },
    data() {
        return {
            colunas: [],
            linhas: [],
            paginacao: {},
            busca: '',
            buscaAplicada: '',
            porPagina: 10,
            opcoesPorPagina: [5, 10, 25, 50],
            pagina: 1,
            // ordenação pelo cabeçalho (feita no servidor): nomecampo + asc|desc
            ordenar: '',
            direcao: 'asc',
            carregando: false,
            erro: false,
            temporizadorBusca: null,
            controlador: null,
            // filtros por coluna vindos do cabeçalho: { nomecampo: texto }
            filtrosColunas: {},
        };
    },
    computed: {
        urlApi() {
            return window.location.origin + '/api/v1' + this.urlSistema;
        },
        temFiltroColuna() {
            return Object.keys(this.filtrosColunas).length > 0;
        },
        opcoesTabela() {
            return {
                servidor: true,
                id: 'listagem:' + this.urlSistema.replace(/\/\d+(?=\/|$)/g, '/*'),
                ferramentas: '.ec-listagem__ferramentas',
                ocultas: this.colunasOcultas,
                aoFiltrar: this.aoFiltrarColunas,
                exportar: this.exportarTudo,
                nomeArquivo: this.rotuloItens,
            };
        },
        totalColunas() {
            return (this.colunas.length || 4) + this.colunasExtras + 1;
        },
        // links do Laravel com "Anterior/Próxima" traduzidos e rótulo acessível
        links() {
            return (this.paginacao.links || []).map((link, i, todos) => {
                let label = link.label;
                let rotulo = 'Página ' + link.label;
                if (i === 0) { label = '&lsaquo;'; rotulo = 'Página anterior'; }
                if (i === todos.length - 1) { label = '&rsaquo;'; rotulo = 'Próxima página'; }
                return Object.assign({}, link, { label, rotulo });
            });
        },
    },
    created() {
        try {
            const salvo = parseInt(localStorage.getItem('ec-por-pagina'), 10);
            if (this.opcoesPorPagina.includes(salvo)) this.porPagina = salvo;
        } catch (e) { /* armazenamento indisponível */ }
        this.carregar(1);
    },
    beforeDestroy() {
        clearTimeout(this.temporizadorBusca);
        if (this.controlador) this.controlador.cancel();
    },
    methods: {
        carregar(pagina) {
            if (pagina) this.pagina = pagina;
            try { localStorage.setItem('ec-por-pagina', this.porPagina); } catch (e) { /* ignora */ }

            // cancela a requisição anterior (ex.: digitação rápida na busca)
            if (this.controlador) this.controlador.cancel();
            this.controlador = axios.CancelToken.source();

            this.carregando = true;
            this.erro = false;
            const termo = this.busca.trim();

            axios.get(this.urlApi, {
                params: this.parametros(this.pagina, this.porPagina),
                cancelToken: this.controlador.token,
            })
                .then(response => {
                    const pagina = response.data[this.chaveDados] || {};
                    this.paginacao = pagina;
                    this.linhas = pagina.data || [];
                    this.colunas = response.data.CamposTabelaExibicao || [];
                    this.buscaAplicada = termo;
                    this.carregando = false;
                    this.$emit('carregado', response.data);
                })
                .catch(error => {
                    if (axios.isCancel(error)) return;
                    console.log(error);
                    this.erro = true;
                    this.linhas = [];
                    this.carregando = false;
                });
        },
        // query string da API: página, busca, ordenação e filtros por coluna (filtros[campo]=texto)
        parametros(pagina, porPagina) {
            const termo = this.busca.trim();
            const params = {
                page: pagina,
                por_pagina: porPagina,
                busca: termo || undefined,
                ordenar: this.ordenar || undefined,
                direcao: this.ordenar ? this.direcao : undefined,
            };
            for (const campo in this.filtrosColunas) params['filtros[' + campo + ']'] = this.filtrosColunas[campo];
            return params;
        },
        // v-ec-datatable: filtros do cabeçalho ([{ rotulo, texto }]) viram filtros[nomecampo]
        aoFiltrarColunas(lista) {
            const filtros = {};
            for (const filtro of lista) {
                const coluna = this.colunas.find(c => c.descricaocoluna === filtro.rotulo);
                if (coluna && filtro.texto) filtros[coluna.nomecampo] = filtro.texto;
            }
            this.filtrosColunas = filtros;
            this.carregar(1);
        },
        // v-ec-datatable: exporta todos os registros da busca/filtros atuais (lotes de 500)
        async exportarTudo({ colunas }) {
            const exportadas = this.colunas.filter(c => colunas.includes(c.descricaocoluna));
            const linhas = [];
            let pagina = 1;
            let ultima = 1;
            do {
                const response = await axios.get(this.urlApi, { params: this.parametros(pagina, 500) });
                const dados = response.data[this.chaveDados] || {};
                ultima = dados.last_page || 1;
                for (const item of dados.data || []) {
                    linhas.push(exportadas.map(c => this.valorVazio(item[c.nomecampo]) ? '' : item[c.nomecampo]));
                }
                pagina++;
            } while (pagina <= ultima);
            return { cabecalho: exportadas.map(c => c.descricaocoluna), linhas };
        },
        // recarrega a página atual (usado após exclusões)
        recarregar() {
            // se a página ficou vazia após excluir o último item, volta uma
            if (this.linhas.length === 1 && this.pagina > 1) this.pagina--;
            this.carregar();
        },
        buscarComAtraso() {
            clearTimeout(this.temporizadorBusca);
            this.temporizadorBusca = setTimeout(() => this.carregar(1), 350);
        },
        limparBusca() {
            if (!this.busca && !this.buscaAplicada) return;
            this.busca = '';
            clearTimeout(this.temporizadorBusca);
            this.carregar(1);
        },
        ordenarPor(campo) {
            this.direcao = this.ordenar === campo && this.direcao === 'asc' ? 'desc' : 'asc';
            this.ordenar = campo;
            this.carregar(1);
        },
        irPara(link) {
            if (!link.url || link.active || this.carregando) return;
            const pagina = new URL(link.url).searchParams.get('page');
            this.carregar(parseInt(pagina, 10) || 1);
        },
        abrirLinha(item, evento) {
            if (!this.linkLinha) return;
            // cliques em botões/links/campos da linha seguem o próprio comportamento
            if (evento.target.closest('a, button, input, select, label')) return;
            // não abre se o usuário estiver selecionando texto para copiar
            if (window.getSelection && String(window.getSelection())) return;
            const url = this.linkLinha(item);
            if (!url) return;
            if (evento.ctrlKey || evento.metaKey) window.open(url, '_blank');
            else window.location.href = url;
        },
        alinhamento(coluna) {
            return { left: 'left', right: 'right' }[coluna.alinhamentotexto] || 'center';
        },
        ehAtivo(valor) {
            return valor === 1 || valor === '1' || valor === true || String(valor).toLowerCase() === 'ativo';
        },
        valorVazio(valor) {
            return valor === null || valor === undefined || valor === '';
        },
    },
};
</script>
